import * as React from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { DateTimePicker } from "@/components/DateTimePicker";
import { AllowancePreviewCard } from "@/components/requests/AllowancePreviewCard";
import { BillEditFields, BillsSummaryCard } from "@/components/requests/DetailCard";
import { CurrencyConversionCard } from "@/components/requests/CurrencyConversionCard";
import { TravelTypePicker } from "@/components/requests/TravelTypePicker";
import { TripLocationFields } from "@/components/requests/TripLocationFields";
import { toIso, toLocalDatetimeInput } from "@/lib/datetime";
import { cn } from "@/lib/utils";
import { EMPTY_TRIP_LOCATION, TripLocationValue, validateTripLocation } from "@/lib/trip";
import {
  ApiError,
  fetchRequestLinkedAdvance,
  fetchReviewAllowedCountries,
  fetchReviewPreviewEstimate,
  fetchTravelRequest,
  reviewEditExpense
} from "../../api";
import {
  Advance,
  AllowancePreview,
  AllowedCountry,
  CLAIM_METHOD_LABEL,
  OTHER_CLAIM_TYPE_LABEL,
  TravelType,
  TravelRequestSummary
} from "../../types";

export interface ReviewRequestFields {
  country: string;
  travelType: TravelType;
  originCountry: string | null;
  startCity: string | null;
  endCity: string | null;
  departureAt: string;
  arrivalAt: string;
  flightDetails: string;
  currency: string;
  currencyAutoSelected: boolean;
}

/** Editable version of RequestDetailDialog, for an approver's Review
 *  action: edit the request's own trip/claim fields, save, and approve in
 *  the same action. Shows a live Full/half-day + Estimated-amount preview
 *  (AllowancePreviewCard, fed by fetchReviewPreviewEstimate) as the approver
 *  edits dates/country/travel type - purely a read-only preview, never
 *  authoritative for what gets saved. The actual save still goes through the
 *  SERVER's own re-derivation (requests_api.py's _recompute_review_estimate,
 *  which also rejects a date edit it can't resolve - e.g. arrival not after
 *  departure - rather than silently saving stale numbers), so what's shown
 *  here always matches what Save will actually store; this dialog doesn't
 *  duplicate that maths, only displays its live-fetched result. Bills are
 *  shown with the same BillsSummaryCard RequestDetailDialog uses
 *  (Reimbursement always; Fixed Allowance as tracking-only when it has any),
 *  so an approver can see what they're approving and correct a bill's
 *  category/amount/currency/description in place. Adding or removing bills
 *  stays the employee's job via Resubmit.
 *
 *  Single-screen form, not the multi-step wizard (see NewRequest.tsx) -
 *  the wizard's step/draft/linked-advance machinery all assume the
 *  signed-in user IS the requester, which isn't true here. */
export function ReviewRequestDialog({
  travelRequestId,
  open,
  onOpenChange,
  onSave,
  saving
}: {
  travelRequestId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (fields: ReviewRequestFields) => void;
  saving: boolean;
}) {
  const [detail, setDetail] = React.useState<TravelRequestSummary | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  // Only needed for the reconciliation ledger's advance-side figures (see
  // CurrencyConversionCard below) - detail itself only carries
  // linkedAdvanceId, not the advance's own per-currency amounts.
  const [linkedAdvance, setLinkedAdvance] = React.useState<Advance | null>(null);
  // True while BillsSummaryCard's own inline bill-edit form is open - Save
  // (which approves the whole request) is disabled while this is true, so
  // an in-progress bill correction can't be silently discarded by clicking
  // past it (see BillsSummaryCard's own onEditingChange doc comment).
  const [editingExpense, setEditingExpense] = React.useState(false);

  const [travelType, setTravelType] = React.useState<TravelType | "">("");
  const [location, setLocation] = React.useState<TripLocationValue>(EMPTY_TRIP_LOCATION);
  const [departureAt, setDepartureAt] = React.useState("");
  const [arrivalAt, setArrivalAt] = React.useState("");
  const [flightDetails, setFlightDetails] = React.useState("");
  const [currency, setCurrency] = React.useState("");

  const [allowedCountries, setAllowedCountries] = React.useState<AllowedCountry[]>([]);
  const [countriesLoading, setCountriesLoading] = React.useState(false);
  const [currencyAutoSelected, setCurrencyAutoSelected] = React.useState(false);

  // Live Full/half-day + Estimated-amount preview - see
  // fetchReviewPreviewEstimate's own doc comment. Without this, an approver
  // editing dates had no way to see the impact of their edit until AFTER
  // saving (and previously, a bad date edit silently left the stale
  // original numbers in place with no indication anything was wrong at all
  // - see requests_api.py's _recompute_review_estimate). Not authoritative
  // for what gets saved - the server recomputes this exact same figure
  // again at save time, so a stale preview (e.g. the fetch still in
  // flight) can never cause a mismatched save.
  const [preview, setPreview] = React.useState<AllowancePreview | null>(null);
  const [previewLoading, setPreviewLoading] = React.useState(false);

  React.useEffect(() => {
    if (!open || !travelRequestId) {
      setDetail(null);
      setLoadError(null);
      return;
    }
    setLoading(true);
    setLoadError(null);
    fetchTravelRequest(travelRequestId)
      .then((d) => {
        setDetail(d);
        setTravelType(d.travelType || "");
        setLocation({
          country: d.country,
          originCountry: d.originCountry || "",
          startCity: d.startCity || "",
          endCity: d.endCity || ""
        });
        setDepartureAt(toLocalDatetimeInput(d.departureAt));
        setArrivalAt(toLocalDatetimeInput(d.arrivalAt));
        setFlightDetails(d.flightDetails || "");
        setCurrency(d.currency);
        setCurrencyAutoSelected(d.currencyAutoSelected);
      })
      .catch((err) =>
        setLoadError(err instanceof ApiError ? err.message : "Could not load this request.")
      )
      .finally(() => setLoading(false));
  }, [open, travelRequestId]);

  React.useEffect(() => {
    if (!detail?.linkedAdvanceId || !travelRequestId) {
      setLinkedAdvance(null);
      return;
    }
    fetchRequestLinkedAdvance(travelRequestId)
      .then(setLinkedAdvance)
      .catch(() => setLinkedAdvance(null));
  }, [detail?.linkedAdvanceId, travelRequestId]);

  React.useEffect(() => {
    if (!travelType || !travelRequestId) {
      setAllowedCountries([]);
      return;
    }
    setCountriesLoading(true);
    fetchReviewAllowedCountries(travelRequestId, travelType, detail?.claimMethod)
      .then(setAllowedCountries)
      .catch(() => setAllowedCountries([]))
      .finally(() => setCountriesLoading(false));
    // detail?.claimMethod is intentionally excluded - the claim method
    // itself isn't editable in Review (it decides which stored fields/
    // routes apply, e.g. bills vs. none, and changing it mid-approval
    // would be a much bigger structural edit than a trip-detail fix), only
    // read once to narrow the country list consistently with what the
    // employee's own wizard would have shown for this request's claim
    // method. Request-scoped (fetchReviewAllowedCountries), NOT the
    // signed-in approver's own designation - see that function's doc
    // comment for why the generic fetchAllowedCountries was wrong here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [travelType, travelRequestId]);

  // Currency is DERIVED from the chosen country's policy rate, never its own
  // free-editable field - same model as NewRequest.tsx's wizard (see that
  // file's own country/currency-sync effect). A standalone editable
  // Currency input here, alongside TripLocationFields' own read-only
  // Currency display, was a duplicate, contradictable field; this syncs the
  // same way the wizard's full-edit (isResubmit) case does, since Review
  // lets the country itself change too.
  //
  // Only resyncs once the approver has actually changed the country away
  // from what the document loaded with (location.country !== detail.country)
  // - NOT merely "does allowedCountries' rate differ from local state",
  // which raced against the async load: `currency` starts at its initial ""
  // and only becomes the real value inside fetchTravelRequest's .then(), so
  // comparing against it here could run before that assignment landed and
  // wrongly flip currencyAutoSelected to true on a plain load where the
  // approver touched nothing - exactly the "edited a field they never
  // touched" bug this fixes. A country not in allowedCountries (e.g. the
  // list still fetching) leaves currency as-is rather than blanking it.
  React.useEffect(() => {
    if (countriesLoading || !location.country || !detail) return;
    if (location.country === detail.country) return;
    const match = allowedCountries.find((c) => c.country === location.country);
    if (match && match.currency !== currency) {
      setCurrency(match.currency);
      setCurrencyAutoSelected(true);
    }
  }, [allowedCountries, countriesLoading, location.country, currency, detail]);

  // Debounced live preview (400ms, same as NewRequest.tsx's own wizard
  // preview) - fires whenever the fields the calculation depends on
  // change, not on every keystroke. Never fires for a currently-invalid
  // date pair (arrival not after departure) - that's shown as a form
  // error instead of a confusing preview-fetch failure, and it would
  // otherwise refetch on every character typed while the approver is
  // mid-edit between two valid dates.
  const canPreview = !!(
    travelType &&
    location.country &&
    departureAt &&
    arrivalAt &&
    new Date(arrivalAt) > new Date(departureAt)
  );
  React.useEffect(() => {
    if (!canPreview || !travelRequestId) {
      setPreview(null);
      return;
    }
    const timer = setTimeout(() => {
      setPreviewLoading(true);
      fetchReviewPreviewEstimate(travelRequestId, {
        country: location.country,
        travelType: travelType as TravelType,
        departureAt: toIso(departureAt),
        arrivalAt: toIso(arrivalAt)
      })
        .then(setPreview)
        .catch(() => setPreview(null))
        .finally(() => setPreviewLoading(false));
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canPreview, travelRequestId, location.country, travelType, departureAt, arrivalAt]);

  // Saves a single bill's category/amount/description immediately (its own
  // round trip to review_edit_expense), independent of the trip-field Save
  // & approve button below - a bill correction doesn't need to wait for the
  // rest of the form, and the request isn't approved just by editing a
  // bill. Refreshes `detail` from the response so the card (and the Total/
  // exceeds-policy figures above it) reflect the edit right away.
  const handleEditExpense = async (expenseId: string, fields: BillEditFields) => {
    if (!travelRequestId) return;
    // An Other reimbursement bill's own fields go up flat, beside the rest;
    // a travel bill has none, so its payload is unchanged.
    const { other, ...rest } = fields;
    const updated = await reviewEditExpense(travelRequestId, expenseId, { ...rest, ...(other ?? {}) });
    setDetail(updated);
  };

  // An Other reimbursement (City travel / Miscellaneous) has no trip: its
  // Review edits Purpose and the bills only (the server ignores anything
  // else for it - see requests_api.py's review_approval).
  const isOther = detail?.requestKind === "other";
  const otherType = isOther ? detail?.claimType ?? null : null;

  // Clicking "Save & approve" only validates and opens the confirmation
  // dialog below - the actual onSave() call (which commits the edit AND
  // approves the request in one action, per this component's own docstring)
  // only fires from that dialog's Confirm, so a misclick on the main button
  // can't silently approve someone else's request with unreviewed edits.
  const [confirmingSave, setConfirmingSave] = React.useState(false);

  // Validation failures are toasted (top-right, red - see
  // components/ui/sonner.tsx) rather than an inline message at the end of
  // the scrollable form - that used to sit below the Bills card, so
  // clicking Continue from higher up the form (e.g. right after editing
  // the dates) showed an error the approver couldn't see without first
  // scrolling down to find it. A toast is visible immediately regardless
  // of scroll position or which field triggered it.
  const handleSaveClick = () => {
    if (editingExpense) {
      toast.error(
        "You're editing a bill. Save your changes, or close the edit form, before continuing."
      );
      return;
    }
    if (isOther) {
      if (!flightDetails.trim()) {
        toast.error("Purpose can't be empty.");
        return;
      }
      setConfirmingSave(true);
      return;
    }
    if (!travelType) {
      toast.error("Select Domestic or International.");
      return;
    }
    const locationError = validateTripLocation(travelType, location);
    if (locationError) {
      toast.error(locationError);
      return;
    }
    if (!departureAt || !arrivalAt) {
      toast.error("Departure and arrival are both required.");
      return;
    }
    if (new Date(arrivalAt) <= new Date(departureAt)) {
      toast.error("Arrival must be after departure.");
      return;
    }
    // Same completed-trip rule create_my_request enforces at submission -
    // mirrored here so an approver sees it immediately rather than only
    // after Continue round-trips to the server (which still has the
    // authoritative check in _recompute_review_estimate).
    if (detail?.claimMethod === "reimbursement") {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const departureDay = new Date(departureAt);
      departureDay.setHours(0, 0, 0, 0);
      const arrivalDay = new Date(arrivalAt);
      arrivalDay.setHours(0, 0, 0, 0);
      if (departureDay > today || arrivalDay > today) {
        toast.error(
          "Reimbursement claims a completed trip - departure and arrival can't be in the future."
        );
        return;
      }
    }
    if (!currency.trim()) {
      toast.error("Currency can't be empty.");
      return;
    }
    setConfirmingSave(true);
  };

  const confirmSave = () => {
    setConfirmingSave(false);
    if (isOther && detail) {
      // Only flightDetails (Purpose) is applied for an Other request; the
      // rest is sent back unchanged to satisfy the shared shape.
      onSave({
        country: detail.country,
        travelType: detail.travelType as TravelType,
        originCountry: null,
        startCity: null,
        endCity: null,
        departureAt: detail.departureAt,
        arrivalAt: detail.arrivalAt,
        flightDetails: flightDetails.trim(),
        currency: detail.currency,
        currencyAutoSelected: detail.currencyAutoSelected
      });
      return;
    }
    onSave({
      country: location.country,
      travelType: travelType as TravelType,
      originCountry: travelType === "international" ? location.originCountry : null,
      startCity: travelType === "domestic" ? location.startCity.trim() : null,
      endCity: travelType === "domestic" ? location.endCity.trim() : null,
      departureAt: toIso(departureAt),
      arrivalAt: toIso(arrivalAt),
      flightDetails: flightDetails.trim(),
      currency: currency.trim().toUpperCase(),
      currencyAutoSelected
    });
  };

  // Widen whenever the ledger itself renders at all (any bill or advance -
  // see CurrencyConversionCard's own hasAnyCurrency), not just 2+ currencies
  // - its table is the same min-w-[57.5rem] regardless of row count, and a
  // single-currency row re-pointed at a different settlement currency still
  // needs the full width (see RequestDetailDialog's identical fix).
  const ledgerRenders = !!detail && (detail.expenses.length > 0 || !!detail.linkedAdvanceId);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className={cn(
          "flex w-full flex-col gap-0 sm:max-w-xl",
          // Room for the reconciliation table's min-w-[57.5rem] without a
          // horizontal scroll on laptop/desktop - same as RequestDetailDialog.
          ledgerRenders && "lg:max-w-[65rem]"
        )}
      >
        <SheetHeader>
          <SheetTitle>Review & approve</SheetTitle>
          <SheetDescription>
            Fix any of the fields below, then save - this approves the request
            with your edits in the same action.
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="-mx-6 mt-4 min-w-0 flex-1">
          {/* grid + minmax(0,1fr), not a plain block div - see
              RequestDetailDialog.tsx's identical fix for why: Radix's
              ScrollArea wraps this in an internal `display: table` div
              sized to ITS content's max-content width, so the reconciliation
              ledger's deliberately min-w-[57.5rem] table (CurrencyConversionCard.tsx)
              would otherwise inflate that wrapper and the whole sheet instead
              of being clipped and scrolled in place by its own
              overflow-x-auto. */}
          <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] px-6">
            <div className="space-y-4">
            {loading && (
              <div className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            )}

            {loadError && <p className="text-sm text-destructive">{loadError}</p>}

            {!loading && detail && (
              <>
                {/* The sheet widens to lg:max-w-[65rem] whenever the ledger
                    renders (see ledgerRenders above) - an extra column there
                    fills that width instead of leaving this 2-column grid's
                    fields stranded with empty space beside them. */}
                <div className={cn("grid gap-3 sm:grid-cols-2", ledgerRenders && "lg:grid-cols-3")}>
                  <div className="space-y-1.5">
                    <Label htmlFor="review-request-designation">
                      Designation
                    </Label>
                    <Input
                      id="review-request-designation"
                      value={detail.designationAtSubmission || "-"}
                      disabled
                      readOnly
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="review-request-claim-method">
                      {isOther ? "Claim type" : "Claim method"}
                    </Label>
                    <Input
                      id="review-request-claim-method"
                      value={
                        isOther
                          ? otherType ? OTHER_CLAIM_TYPE_LABEL[otherType] : "-"
                          : CLAIM_METHOD_LABEL[detail.claimMethod]
                      }
                      disabled
                      readOnly
                    />
                  </div>
                  {isOther && (
                    <>
                      <div className="space-y-1.5">
                        <Label htmlFor="review-request-country">Country</Label>
                        <Input id="review-request-country" value={detail.country || "-"} disabled readOnly />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="review-request-currency">Currency</Label>
                        <Input id="review-request-currency" value={detail.currency || "-"} disabled readOnly />
                      </div>
                    </>
                  )}
                </div>

                {!isOther && (
                <>
                <TravelTypePicker
                  value={travelType}
                  onChange={(t) => {
                    setTravelType(t);
                    setLocation(EMPTY_TRIP_LOCATION);
                  }}
                />

                {travelType && (
                  <TripLocationFields
                    travelType={travelType}
                    value={location}
                    onChange={setLocation}
                    allowedCountries={allowedCountries}
                    loading={countriesLoading}
                    emptyHint="No countries are configured for this travel type yet."
                    idPrefix="review-request"
                  />
                )}

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>Departure</Label>
                    <DateTimePicker
                      value={departureAt}
                      onChange={setDepartureAt}
                      maxDate={
                        detail?.claimMethod === "reimbursement"
                          ? new Date()
                          : undefined
                      }
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Arrival</Label>
                    <DateTimePicker
                      value={arrivalAt}
                      onChange={setArrivalAt}
                      maxDate={
                        detail?.claimMethod === "reimbursement"
                          ? new Date()
                          : undefined
                      }
                    />
                  </div>
                </div>

                {(canPreview || previewLoading) && (
                  <AllowancePreviewCard
                    preview={preview}
                    loading={previewLoading}
                    currency={detail.currency}
                    expenses={detail.expenses}
                  />
                )}
                </>
                )}

                <div className="space-y-1.5">
                  <Label htmlFor="review-request-flight">{isOther ? "Purpose" : "Purpose of travel"}</Label>
                  <Textarea
                    id="review-request-flight"
                    value={flightDetails}
                    onChange={(e) => setFlightDetails(e.target.value)}
                    rows={3}
                  />
                </div>

                {(detail.claimMethod === "reimbursement" ||
                  detail.expenses.length > 0) && (
                  <BillsSummaryCard
                    requestId={detail.id}
                    expenses={detail.expenses}
                    currency={detail.currency}
                    estimatedAllowanceAmount={detail.estimatedAllowanceAmount}
                    limitsByCurrency={detail.policyLimitsByCurrencyAtSubmission}
                    trackingOnly={detail.claimMethod === "fixed_allowance"}
                    onEditExpense={handleEditExpense}
                    onEditingChange={setEditingExpense}
                    noLimit={isOther}
                    billFields={otherType ?? "travel"}
                  />
                )}

                {/* Editable here (unlike RequestDetailDialog's read-only
                    copy) - this IS the review/approve action, so the
                    approver can set a settlement currency per row or
                    override an FX rate (with proof) before approving. See
                    CurrencyConversionCard's own doc comment. */}
                <CurrencyConversionCard
                  travelRequestId={detail.id}
                  expenses={detail.expenses}
                  advanceAmounts={linkedAdvance?.amounts}
                  fxOverrides={detail.fxOverrides}
                  settlementTargetByCurrency={detail.settlementTargetByCurrency}
                  defaultTargetCurrency={detail.currency}
                  departureDate={departureAt || detail.departureAt || detail.createdAt}
                  claimMethod={detail.claimMethod}
                  // Fixed Allowance only - without this the card's ledger
                  // never folds the allowance into the defaultTargetCurrency
                  // row (see CurrencyConversionCard's own ledgerExpenses doc
                  // comment), so the approver's own "Net payable"/"Owed by"
                  // tile silently fell back to the OLD bills-only
                  // calculation here too (this prop was missing entirely
                  // from this call, same gap as RequestDetailDialog.tsx's
                  // read-only copy).
                  estimatedAllowanceAmount={
                    detail.claimMethod === "fixed_allowance" ? detail.estimatedAllowanceAmount : undefined
                  }
                  onOverridesChanged={setDetail}
                />
              </>
            )}
            </div>
          </div>
        </ScrollArea>

        <SheetFooter className="mt-4 gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSaveClick} disabled={loading || saving || !detail || editingExpense}>
            {saving ? "Saving…" : "Continue"}
          </Button>
        </SheetFooter>
      </SheetContent>

      <ConfirmDialog
        open={confirmingSave}
        onOpenChange={setConfirmingSave}
        title="Save these edits and approve this request?"
        description="Your changes will be applied and the request moves forward as Approved in the same action - this can't be undone from here."
        confirmLabel="Save & approve"
        pending={saving}
        onConfirm={confirmSave}
      />
    </Sheet>
  );
}
